import { getAuditImageBlob } from '../utils/api';

/** @typedef {'idle'|'loading'|'ready'|'error'} CacheStatus */

/**
 * Session-scoped audit recognition image cache.
 * Deduplicates in-flight requests and manages Object URL ref-counting.
 */
const entries = new Map();
const inFlight = new Map();
const listeners = new Map();

const isBlobUrl = (value) => typeof value === 'string' && value.startsWith('blob:');

const notify = (auditId) => {
  const set = listeners.get(auditId);
  if (set) {
    set.forEach((fn) => {
      try {
        fn();
      } catch (_) {
        // ignore subscriber errors
      }
    });
  }
};

const ensureEntry = (auditId) => {
  let entry = entries.get(auditId);
  if (!entry) {
    entry = { url: undefined, refCount: 0, status: 'idle' };
    entries.set(auditId, entry);
  }
  return entry;
};

/**
 * @returns {string|null|undefined}
 * undefined = not loaded yet, null = failed/missing, string = object URL
 */
export const peek = (auditId) => {
  if (!auditId) return undefined;
  const entry = entries.get(auditId);
  if (!entry) return undefined;
  if (entry.status === 'ready') return entry.url;
  if (entry.status === 'error') return null;
  return undefined;
};

export const acquire = (auditId) => {
  if (!auditId) return;
  const entry = ensureEntry(auditId);
  entry.refCount += 1;
};

export const release = (auditId) => {
  if (!auditId) return;
  const entry = entries.get(auditId);
  if (!entry) return;
  entry.refCount -= 1;
  if (entry.refCount > 0) return;
  if (isBlobUrl(entry.url)) {
    try {
      URL.revokeObjectURL(entry.url);
    } catch (_) {
      // best-effort cleanup
    }
  }
  entries.delete(auditId);
  inFlight.delete(auditId);
  listeners.delete(auditId);
};

export const subscribe = (auditId, callback) => {
  if (!auditId || typeof callback !== 'function') {
    return () => {};
  }
  if (!listeners.has(auditId)) {
    listeners.set(auditId, new Set());
  }
  listeners.get(auditId).add(callback);
  return () => {
    listeners.get(auditId)?.delete(callback);
  };
};

export const getOrFetch = async (auditId) => {
  if (!auditId) return null;
  const cached = peek(auditId);
  if (cached !== undefined) return cached;
  if (inFlight.has(auditId)) return inFlight.get(auditId);

  const entry = ensureEntry(auditId);
  entry.status = 'loading';
  notify(auditId);

  const promise = (async () => {
    try {
      const blob = await getAuditImageBlob(auditId);
      if (!blob || blob.size === 0 || (blob.type && !blob.type.startsWith('image/'))) {
        throw new Error('Invalid or empty image response');
      }
      const url = URL.createObjectURL(blob);
      const current = entries.get(auditId);
      if (current) {
        current.url = url;
        current.status = 'ready';
      }
      notify(auditId);
      return url;
    } catch (err) {
      const current = entries.get(auditId);
      if (current) {
        current.url = null;
        current.status = 'error';
      }
      notify(auditId);
      return null;
    } finally {
      inFlight.delete(auditId);
    }
  })();

  inFlight.set(auditId, promise);
  return promise;
};

export const clearAll = () => {
  entries.forEach((entry) => {
    if (isBlobUrl(entry.url)) {
      try {
        URL.revokeObjectURL(entry.url);
      } catch (_) {
        // best-effort cleanup
      }
    }
  });
  entries.clear();
  inFlight.clear();
  listeners.clear();
};